import { createContext, Dispatch, SetStateAction, useContext } from "react";
import { ListboxProps } from "../Listbox";

/**
 * Context properties for the Listbox component
 * @template T - The type of additional properties for items
 */
export type ListboxContextProps<T> = {
  /** ID of the currently focused option */
  currentFocus?: string;
  /** Function to set the currently focused option */
  setCurrentFocus: Dispatch<
    SetStateAction<ListboxContextProps<T>["currentFocus"]>
  >;
  /** Currently selected option(s) */
  selected: ListboxProps<T>["defaultSelected"];
  /** Function to set the selected option(s) */
  setSelected: Dispatch<SetStateAction<ListboxContextProps<T>["selected"]>>;
  /** List of available option elements */
  options: NodeListOf<HTMLElement> | undefined;
  /** Current selection mode */
  selectionMode: Exclude<ListboxProps<T>["selectionMode"], undefined>;
  /** Callback when selection changes */
  onSelectionChange: ListboxProps<T>["onSelectionChange"];
  /** Whether auto-select on focus is disabled */
  disableAutoSelectOnFocus: boolean;
  /** Array of items if using items prop */
  items?: unknown[];
  /** Whether the component is controlled */
  controlled?: boolean;
};

/**
 * Context for sharing listbox state between components
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ListboxContext = createContext<ListboxContextProps<any> | null>(
  null,
);

/**
 * Hook to access the listbox context
 * @returns The listbox context
 * @throws Error if used outside of a Listbox component
 */
export const useListbox = () => {
  const context = useContext(ListboxContext);

  if (context == null) {
    throw new Error("useListbox must be wrapped in <Listbox />");
  }

  return context;
};
